import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Legend,
} from "recharts";
import {
    Eye, Users, ShoppingCart, CreditCard, DollarSign, Activity, Globe2, MousePointerClick,
    Smartphone, Laptop, Tablet, ChevronRight, Download, RefreshCw,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const REFRESH_MS = 20000;
const RANGES = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "7d", label: "7 days" },
    { key: "30d", label: "30 days" },
    { key: "90d", label: "90 days" },
    { key: "365d", label: "12 months" },
];

const fmtMoney = (n) => `A$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;
const fmtDur = (sec) => {
    const s = Math.floor(Number(sec || 0));
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

const useAdminGet = (path, params, deps = [], { interval } = {}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const headersRef = useRef(null);
    const fetchData = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const { data: resp } = await api.get(path, { params, headers: headersRef.current });
            setData(resp);
            setErr(null);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to load");
        } finally {
            if (!silent) setLoading(false);
        }
    };
    useEffect(() => {
        headersRef.current = { "x-admin-token": localStorage.getItem("admin_token") };
        fetchData();
        if (interval) {
            const id = setInterval(() => fetchData(true), interval);
            return () => clearInterval(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return { data, loading, err, refetch: () => fetchData(false) };
};

/* ────────────────────────────── Live tiles ────────────────────────────── */

const LiveTile = ({ icon: Icon, label, value, sub, glow, testid }) => (
    <div
        className="rounded-2xl p-4 border border-white/5 bg-black/30 relative overflow-hidden hover-lift"
        data-testid={testid}
    >
        {glow && (
            <div
                className="absolute -top-12 -right-12 w-28 h-28 rounded-full pointer-events-none opacity-60"
                style={{ background: "radial-gradient(circle, var(--tiffany-soft), transparent 65%)" }}
            />
        )}
        <div className="flex items-center justify-between relative">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</span>
            <Icon size={14} className={glow ? "text-[var(--tiffany)]" : "text-[var(--gold)]"} />
        </div>
        <div className="mt-3 font-display text-2xl font-extrabold text-white">{value}</div>
        {sub && <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">{sub}</div>}
    </div>
);

/* ────────────────────────────── Activity feed ────────────────────────────── */

const EVENT_COLORS = {
    page_view: "var(--text-secondary)",
    product_view: "var(--gold)",
    add_to_cart: "var(--tiffany)",
    checkout_start: "#F59E0B",
    purchase: "#10B981",
};
const EVENT_LABELS = {
    page_view: "Page view",
    product_view: "Product view",
    add_to_cart: "Add to cart",
    checkout_start: "Checkout started",
    purchase: "Order placed",
};
const ActivityFeed = () => {
    const { data, loading } = useAdminGet("/admin/analytics/activity", { limit: 30 }, [], { interval: REFRESH_MS });
    const events = data?.events || [];
    return (
        <div className="rounded-2xl border border-white/5 bg-black/30 p-4 md:p-5 h-[480px] flex flex-col" data-testid="activity-feed">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-sm font-bold text-white flex items-center gap-2">
                    <Activity size={14} className="text-[var(--tiffany)]" /> Live activity feed
                </h3>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                    {loading ? "Updating..." : `${events.length} events`}
                </span>
            </div>
            <div className="overflow-y-auto flex-1 space-y-2 pr-1" style={{ scrollbarWidth: "thin" }}>
                {events.length === 0 && (
                    <div className="text-sm text-[var(--text-muted)] text-center py-12">
                        Waiting for the first event... Browse the storefront in another tab to see events stream in.
                    </div>
                )}
                {events.map((e, i) => (
                    <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-black/20 border border-white/5" data-testid={`activity-row-${i}`}>
                        <span
                            className="mt-1 w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ background: EVENT_COLORS[e.event_type] || "var(--text-muted)" }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-white font-semibold">{EVENT_LABELS[e.event_type] || e.event_type}</span>
                                {e.product_name && (
                                    <span className="text-[var(--gold)] truncate">· {e.product_name}</span>
                                )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                                {e.path && <span className="truncate max-w-[180px]">{e.path}</span>}
                                {e.device && <span>· {e.device}</span>}
                                {e.browser && <span>· {e.browser}</span>}
                                {e.country && <span>· {e.country}</span>}
                            </div>
                        </div>
                        <span className="text-[10px] font-mono text-[var(--text-muted)] shrink-0">
                            {new Date(e.created_at).toLocaleTimeString()}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ────────────────────────────── Traffic chart ────────────────────────────── */

const TrafficChart = ({ range }) => {
    const { data, loading } = useAdminGet("/admin/analytics/traffic", { range }, [range]);
    const series = data?.series || [];
    const t = data?.totals || {};
    return (
        <div className="rounded-2xl border border-white/5 bg-black/30 p-4 md:p-5" data-testid="traffic-chart">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
                <div>
                    <h3 className="font-display text-sm font-bold text-white">Traffic overview</h3>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                        {loading ? "Loading..." : `${data?.granularity || "day"} buckets · ${range}`}
                    </p>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-7 gap-3 text-sm">
                    <Metric label="Visitors" v={fmtNum(t.visitors)} />
                    <Metric label="Sessions" v={fmtNum(t.sessions)} />
                    <Metric label="Page views" v={fmtNum(t.page_views)} />
                    <Metric label="Orders" v={fmtNum(t.orders)} />
                    <Metric label="Revenue" v={fmtMoney(t.revenue)} />
                    <Metric label="Conv rate" v={fmtPct(t.conversion_rate)} />
                    <Metric label="AOV" v={fmtMoney(t.avg_order_value)} />
                </div>
            </div>
            <div className="h-[260px] min-w-0">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <LineChart data={series} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="bucket" stroke="#888" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#888" tick={{ fontSize: 10 }} />
                        <Tooltip
                            contentStyle={{ background: "#0A0604", border: "1px solid #E9BC0B22", borderRadius: 8, fontSize: 12 }}
                            labelStyle={{ color: "#E9BC0B" }}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="visitors" stroke="#4FD1C5" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="sessions" stroke="#E9BC0B" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="orders" stroke="#10B981" strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

const Metric = ({ label, v }) => (
    <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
        <div className="text-base font-bold text-white">{v}</div>
    </div>
);

/* ────────────────────────────── Funnel ────────────────────────────── */

const Funnel = ({ range }) => {
    const { data } = useAdminGet("/admin/analytics/funnel", { range }, [range]);
    const stages = data?.stages || [];
    const max = Math.max(...stages.map((s) => s.count), 1);
    return (
        <div className="rounded-2xl border border-white/5 bg-black/30 p-4 md:p-5" data-testid="funnel-section">
            <h3 className="font-display text-sm font-bold text-white mb-4">Customer funnel · {range}</h3>
            <div className="space-y-2">
                {stages.map((s) => (
                    <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-white">{s.label}</span>
                            <span className="text-xs font-mono text-[var(--text-muted)]">
                                {fmtNum(s.count)} · <span className="text-[var(--tiffany)]">{fmtPct(s.conversion_from_prev_pct)}</span>
                            </span>
                        </div>
                        <div className="h-2 bg-black/50 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[var(--gold)] to-[var(--tiffany)] transition-all duration-700"
                                style={{ width: `${(s.count / max) * 100}%` }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ────────────────────────────── Products table ────────────────────────────── */

const ProductTable = ({ range }) => {
    const { data, loading } = useAdminGet("/admin/analytics/products-deep", { range }, [range]);
    const items = data?.by_revenue || [];
    return (
        <div className="rounded-2xl border border-white/5 bg-black/30 p-4 md:p-5" data-testid="product-table">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-sm font-bold text-white">Top products · {range}</h3>
                <ExportBtn kind="products" range={range} />
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] text-left">
                            <th className="py-2 font-normal">Product</th>
                            <th className="py-2 font-normal text-right">Views</th>
                            <th className="py-2 font-normal text-right">Adds</th>
                            <th className="py-2 font-normal text-right">Orders</th>
                            <th className="py-2 font-normal text-right">Conv</th>
                            <th className="py-2 font-normal text-right">Revenue</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="py-8 text-center text-[var(--text-muted)] text-sm">
                                {loading ? "Loading..." : "No product activity yet in this window."}
                            </td></tr>
                        )}
                        {items.map((p) => (
                            <tr key={p.slug} className="border-t border-white/5">
                                <td className="py-2.5 flex items-center gap-2">
                                    {p.image && <img src={p.image} alt="" className="w-10 h-7 rounded object-contain" loading="lazy" />}
                                    <span className="text-white">{p.name}</span>
                                </td>
                                <td className="py-2.5 text-right text-[var(--text-secondary)] font-mono">{fmtNum(p.views)}</td>
                                <td className="py-2.5 text-right text-[var(--tiffany)] font-mono">{fmtNum(p.add_to_cart)}</td>
                                <td className="py-2.5 text-right text-emerald-400 font-mono">{fmtNum(p.purchases)}</td>
                                <td className="py-2.5 text-right text-[var(--text-secondary)] font-mono">{fmtPct(p.conversion_rate)}</td>
                                <td className="py-2.5 text-right text-[var(--gold)] font-mono font-semibold">{fmtMoney(p.revenue)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/* ────────────────────────────── Countries + sources tables ────────────────────────────── */

const CountriesTable = ({ range }) => {
    const { data, loading } = useAdminGet("/admin/analytics/countries", { range }, [range]);
    const rows = data?.countries || [];
    return (
        <div className="rounded-2xl border border-white/5 bg-black/30 p-4 md:p-5" data-testid="countries-table">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-sm font-bold text-white flex items-center gap-2">
                    <Globe2 size={14} className="text-[var(--tiffany)]" />
                    Countries · {range}
                </h3>
                <ExportBtn kind="countries" range={range} />
            </div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
                {rows.length === 0 && (
                    <div className="text-sm text-[var(--text-muted)] py-6 text-center">
                        {loading ? "Loading..." : "Country attribution will appear once visitors arrive via Cloudflare or behind a country header."}
                    </div>
                )}
                {rows.map((r) => (
                    <div key={r.country} className="flex items-center justify-between text-sm" data-testid={`country-row-${r.country_code || r.country}`}>
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-muted)] w-8">{r.country_code || "??"}</span>
                            <span className="text-white truncate">{r.country}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            <span className="text-[var(--tiffany)] font-mono text-xs">{fmtNum(r.visitors)}</span>
                            <span className="text-[var(--gold)] font-mono text-xs">{fmtMoney(r.revenue)}</span>
                            <span className="text-[var(--text-muted)] font-mono text-xs w-12 text-right">{fmtPct(r.percent)}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SourcesTable = ({ range }) => {
    const { data, loading } = useAdminGet("/admin/analytics/sources", { range }, [range]);
    const rows = data?.sources || [];
    return (
        <div className="rounded-2xl border border-white/5 bg-black/30 p-4 md:p-5" data-testid="sources-table">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-sm font-bold text-white flex items-center gap-2">
                    <ChevronRight size={14} className="text-[var(--tiffany)]" />
                    Traffic sources · {range}
                </h3>
                <ExportBtn kind="sources" range={range} />
            </div>
            <div className="h-[240px] min-w-0">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <BarChart data={rows.slice(0, 8)} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="source" stroke="#888" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#888" tick={{ fontSize: 10 }} />
                        <Tooltip
                            contentStyle={{ background: "#0A0604", border: "1px solid #E9BC0B22", borderRadius: 8, fontSize: 12 }}
                            labelStyle={{ color: "#E9BC0B" }}
                        />
                        <Bar dataKey="sessions" fill="#4FD1C5" radius={[6, 6, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            {rows.length === 0 && (
                <div className="text-sm text-[var(--text-muted)] text-center pt-2">
                    {loading ? "Loading..." : "Use UTM parameters on your social / email links to break sources down further."}
                </div>
            )}
        </div>
    );
};

/* ────────────────────────────── CSV export button ────────────────────────────── */

const ExportBtn = ({ kind, range }) => {
    const onClick = async () => {
        try {
            const token = localStorage.getItem("admin_token");
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/export/${kind}?range=${range}`;
            const resp = await fetch(url, { headers: { "x-admin-token": token } });
            if (!resp.ok) throw new Error("Export failed");
            const blob = await resp.blob();
            const dl = document.createElement("a");
            dl.href = URL.createObjectURL(blob);
            dl.download = `carbon-labs-${kind}-${range}.csv`;
            dl.click();
            URL.revokeObjectURL(dl.href);
        } catch (e) {
            toast.error("Export failed");
        }
    };
    return (
        <button
            onClick={onClick}
            className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--tiffany)] flex items-center gap-1.5 px-2 py-1 rounded border border-white/10 hover:border-[var(--tiffany)] transition-colors"
            data-testid={`export-${kind}-btn`}
        >
            <Download size={11} /> CSV
        </button>
    );
};

/* ────────────────────────────── Main panel ────────────────────────────── */

export const AdminLiveAnalyticsPanel = () => {
    const [range, setRange] = useState("7d");
    const { data: live, refetch: refreshLive } = useAdminGet("/admin/analytics/live", null, [], { interval: REFRESH_MS });

    const tiles = useMemo(() => ([
        { icon: Users, label: "Visitors online", value: fmtNum(live?.visitors_online), sub: "Last 5 min", glow: true, testid: "tile-visitors-online" },
        { icon: Activity, label: "Active sessions", value: fmtNum(live?.active_sessions), sub: "Last 5 min", testid: "tile-active-sessions" },
        { icon: Eye, label: "Viewing products", value: fmtNum(live?.viewing_products), sub: "Right now", testid: "tile-viewing-products" },
        { icon: MousePointerClick, label: "Active checkouts", value: fmtNum(live?.active_checkouts), sub: "Started, not paid", testid: "tile-active-checkouts" },
        { icon: ShoppingCart, label: "Carts (1h)", value: fmtNum(live?.cart_count_last_hour), sub: "Add to cart count", testid: "tile-carts-1h" },
        { icon: CreditCard, label: "Orders today", value: fmtNum(live?.orders_today), sub: "Paid orders", glow: true, testid: "tile-orders-today" },
        { icon: DollarSign, label: "Revenue today", value: fmtMoney(live?.revenue_today), sub: "Net paid", glow: true, testid: "tile-revenue-today" },
        { icon: Smartphone, label: "Avg session", value: fmtDur(live?.avg_session_duration_seconds), sub: "Trailing 7d", testid: "tile-avg-session" },
        { icon: Laptop, label: "Bounce rate", value: fmtPct(live?.bounce_rate_7d), sub: "Trailing 7d", testid: "tile-bounce" },
        { icon: Tablet, label: "New vs returning", value: `${fmtNum(live?.new_visitors_today)} / ${fmtNum(live?.returning_visitors_today)}`, sub: "Today", testid: "tile-new-returning" },
    ]), [live]);

    return (
        <div className="space-y-6" data-testid="live-analytics-panel">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="font-display text-2xl font-bold text-white tracking-tighter">Live website analytics</h2>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">
                        Auto-refreshes every {REFRESH_MS / 1000}s · Visitor sessions defined as a 30-min idle window
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {RANGES.map((r) => (
                        <button
                            key={r.key}
                            onClick={() => setRange(r.key)}
                            className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${
                                range === r.key
                                    ? "border-[var(--tiffany)] text-[var(--tiffany)] bg-[var(--tiffany-soft)]"
                                    : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                            }`}
                            data-testid={`range-${r.key}`}
                        >
                            {r.label}
                        </button>
                    ))}
                    <button
                        onClick={refreshLive}
                        className="p-1.5 rounded-full border border-white/10 text-[var(--text-secondary)] hover:text-[var(--tiffany)] hover:border-[var(--tiffany)]"
                        aria-label="Refresh"
                        data-testid="live-refresh-btn"
                    >
                        <RefreshCw size={14} />
                    </button>
                </div>
            </div>

            {/* Live tiles */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {tiles.map((t, i) => <LiveTile key={i} {...t} />)}
            </div>

            {/* Traffic chart */}
            <TrafficChart range={range} />

            {/* Activity feed + Funnel side by side on lg */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <ActivityFeed />
                <Funnel range={range} />
            </div>

            {/* Product table */}
            <ProductTable range={range} />

            {/* Countries + Sources */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <CountriesTable range={range} />
                <SourcesTable range={range} />
            </div>
        </div>
    );
};
